/**
 * CREATIVE DETONATOR - DATA MAPPER
 * Single source of truth for DB ↔ App field conversions
 *
 * Purpose:
 * - Eliminates scattered data normalization across 6+ files
 * - Consistent field name handling (snake_case DB → camelCase App)
 * - Single place to add new fields
 *
 * PHASE 1 REFACTORING:
 * - Core normalization logic moved to js/core/models/
 * - This file now delegates to model functions
 * - Legacy functions marked deprecated for backward compatibility
 * - Field accessors retained for transition period
 *
 * @version 3
 */

// ============================================================================
// PHASE 1: Import from new models (primary normalization source)
// ============================================================================
import {
  normalizeIdea as modelNormalizeIdea,
  normalizeIdeas as modelNormalizeIdeas,
  ideaToDb as modelIdeaToDb,
  normalizeSession as modelNormalizeSession,
  normalizeSessions as modelNormalizeSessions,
  normalizeBrand as modelNormalizeBrand,
  normalizeBrands as modelNormalizeBrands,
  brandToDb as modelBrandToDb,
  normalizeProduct as modelNormalizeProduct,
  normalizeProducts as modelNormalizeProducts,
  productToDb as modelProductToDb
} from './models/index.js';

// ============================================================================
// JSDOC TYPE DEFINITIONS
// These types enable VS Code autocomplete and catch field name typos at dev time
// ============================================================================

/**
 * Normalized Idea object (camelCase, all fields resolved)
 * @typedef {Object} NormalizedIdea
 * @property {string} id - UUID
 * @property {string} [sessionId] - Session UUID
 * @property {string} [brandId] - Brand UUID
 * @property {string} [productId] - Product UUID
 * @property {string} [triggerCode] - Trigger code (e.g., "A1")
 * @property {string} format - Format: "print", "video", "social", "digital", "experiential", "campaign"
 * @property {string} [headline] - Main headline text
 * @property {string} [visual] - Visual description
 * @property {string} [whyItWorks] - Explanation of why the idea works
 * @property {string} [imageUrl] - R2 or FAL image URL
 * @property {string} [videoUrl] - R2 or FAL video URL
 * @property {boolean} saved - Whether the idea is saved
 * @property {boolean} isRefined - Whether the idea has been refined
 * @property {string} [sourceFragment] - Source fragment for refinement
 * @property {string} [sourceTruth] - Source truth for refinement
 * @property {Object} [scores] - Scoring data
 * @property {string} [createdAt] - ISO timestamp
 * @property {string} [updatedAt] - ISO timestamp
 * @property {string} [savedAt] - ISO timestamp when saved
 * @property {string} [idea] - Raw idea text (from meta)
 * @property {string} [title] - Idea title (from meta)
 * @property {string} [headlineLabel] - Label for headline field
 * @property {string} [sectionLabel] - Label for visual section
 * @property {string} [critique] - Refinement critique
 * @property {string[]} [alternativeHeadlines] - Alternative headline options
 * @property {number} [refinementCount] - Number of refinements
 * @property {Object} [structure] - Structural data
 * @property {Object[]} [scenes] - Video scenes (VIDEO format)
 * @property {number} [sceneCount] - Number of scenes
 * @property {string} [duration] - Video duration (:15, :30, :60)
 * @property {string} [style] - Video style direction
 * @property {string} [super] - Video end-frame text
 * @property {string[]} [alternativeSupers] - Alternative supers
 * @property {string} [hook] - Social hook (SOCIAL format)
 * @property {string} [sequence] - Social sequence
 * @property {string} [caption] - Social caption
 * @property {string[]} [alternativeHooks] - Alternative hooks
 * @property {Object[]} [frames] - Digital frames (DIGITAL format)
 * @property {string} [interaction] - Digital interaction
 * @property {string} [cta] - Call to action
 * @property {string[]} [alternativeCTAs] - Alternative CTAs
 * @property {string} [setup] - Experiential setup (EXPERIENTIAL format)
 * @property {string} [participation] - Participation element
 * @property {string} [reveal] - Reveal moment
 * @property {string[]} [alternativeReveals] - Alternative reveals
 * @property {string} [campaignLine] - Campaign line (CAMPAIGN format)
 * @property {string} [manifesto] - Campaign manifesto
 * @property {string} [extensions] - Campaign extensions
 * @property {string[]} [alternativeLines] - Alternative campaign lines
 */

/**
 * Normalized Session object (camelCase)
 * @typedef {Object} NormalizedSession
 * @property {string} id - UUID
 * @property {string} userId - User UUID
 * @property {SessionBrief} brief - Session brief data
 * @property {string[]} triggers - Array of trigger codes
 * @property {string[]} formats - Array of format strings
 * @property {string} [brandId] - Brand UUID
 * @property {string} [productId] - Product UUID
 * @property {number} creditsUsed - Credits consumed
 * @property {string} promptVersion - Prompt version used
 * @property {string} provider - AI provider
 * @property {string} model - Model used
 * @property {Object} [meta] - Additional metadata
 * @property {string} createdAt - ISO timestamp
 * @property {string} [updatedAt] - ISO timestamp
 */

/**
 * Session brief data
 * @typedef {Object} SessionBrief
 * @property {string} product - Product name
 * @property {string} benefit - Key benefit
 * @property {string[]} formats - Selected formats
 * @property {string} [productId] - Product UUID
 * @property {Object} [productData] - Full product data
 * @property {Object} [brand] - Brand data
 */

/**
 * Normalized Brand object (camelCase)
 * @typedef {Object} NormalizedBrand
 * @property {string} id - UUID
 * @property {string} code - Short brand code (e.g., "NIKE")
 * @property {string} name - Full brand name
 * @property {string} [industry] - Industry category
 * @property {string} [description] - Brand description
 * @property {string} [archetype] - Brand archetype
 * @property {string} [tone] - Tone of voice
 * @property {string} [voicePrompt] - V5 voice DNA prompt
 * @property {string} [visualPrompt] - V5 visual DNA prompt
 * @property {string[]} [colors] - Brand colors array
 * @property {string} shellColor - Cartridge shell color
 * @property {string} createdAt - ISO timestamp
 * @property {string} [updatedAt] - ISO timestamp
 */

/**
 * Normalized Image object (from gallery)
 * @typedef {Object} NormalizedImage
 * @property {string} id - UUID
 * @property {string} ideaId - Associated idea UUID
 * @property {string} url - Image URL (R2 or FAL)
 * @property {string} [prompt] - Generation prompt
 * @property {string} [provider] - Image provider
 * @property {string} [aspectRatio] - Aspect ratio
 * @property {string} [storagePath] - R2 storage path
 * @property {string} [storageBucket] - Storage bucket
 * @property {number} [fileSize] - File size in bytes
 * @property {string} createdAt - ISO timestamp
 */

/**
 * Normalized Video object (from gallery)
 * @typedef {Object} NormalizedVideo
 * @property {string} id - UUID
 * @property {string} ideaId - Associated idea UUID
 * @property {string} url - Video URL (R2 or FAL)
 * @property {string} [sourceImageUrl] - Source image used
 * @property {string} [prompt] - Generation prompt
 * @property {string} [provider] - Video provider
 * @property {string} [model] - Model used
 * @property {number} [durationSeconds] - Duration in seconds
 * @property {string} [storagePath] - R2 storage path
 * @property {string} [storageBucket] - Storage bucket
 * @property {number} [fileSize] - File size in bytes
 * @property {boolean} isTransferred - Whether transferred to R2
 * @property {string} createdAt - ISO timestamp
 */

// ============================================================================
// FIELD DEFINITIONS (Single source of truth)
// ============================================================================

/**
 * Idea field mappings
 * - db: Database column name (snake_case)
 * - app: Frontend property name (camelCase)
 * - fallbacks: Alternative field names to check
 * - aliases: Same concept with different names
 * - transform: Function to apply during conversion
 * - default: Default value if undefined
 */
const IDEA_FIELDS = {
  id: { db: 'id', app: 'id', transform: String },
  sessionId: { db: 'session_id', app: 'sessionId' },
  triggerCode: { db: 'trigger_code', app: 'triggerCode', fallbacks: ['trigger'] },
  format: { db: 'format', app: 'format', default: 'print' },
  headline: { db: 'headline', app: 'headline' },
  visual: { db: 'visual', app: 'visual', aliases: ['text'] },
  explanation: { db: 'explanation', app: 'whyItWorks', aliases: ['why_it_works'] },
  imageUrl: { db: 'image_url', app: 'imageUrl' },
  videoUrl: { db: 'video_url', app: 'videoUrl' },
  saved: { db: 'is_saved', app: 'saved', transform: Boolean },
  isRefined: { db: 'is_refined', app: 'isRefined', transform: Boolean, default: false },
  sourceFragment: { db: 'source_fragment', app: 'sourceFragment' },
  sourceTruth: { db: 'source_truth', app: 'sourceTruth' },
  scores: { db: 'scores', app: 'scores' },
  brandId: { db: 'brand_id', app: 'brandId' },
  productId: { db: 'product_id', app: 'productId' },
  createdAt: { db: 'created_at', app: 'createdAt' },
  updatedAt: { db: 'updated_at', app: 'updatedAt' },
  savedAt: { db: 'saved_at', app: 'savedAt' }
};

/**
 * Meta fields stored in JSONB `meta` column
 */
const META_FIELDS = {
  idea: { path: 'meta.idea', app: 'idea', default: '' },
  title: { path: 'meta.title', app: 'title' },
  headlineLabel: { path: 'meta.headlineLabel', app: 'headlineLabel', default: 'HEADLINE' },
  sectionLabel: { path: 'meta.sectionLabel', app: 'sectionLabel', default: 'VISUAL' },
  critique: { path: 'meta.critique', app: 'critique' },
  alternativeHeadlines: { path: 'meta.alternativeHeadlines', app: 'alternativeHeadlines', default: [] },
  refinementCount: { path: 'meta.refinementCount', app: 'refinementCount', default: 0 },
  structure: { path: 'meta.structure', app: 'structure' },

  // VIDEO format fields
  scenes: { path: 'meta.scenes', app: 'scenes' },
  sceneCount: { path: 'meta.sceneCount', app: 'sceneCount' },
  duration: { path: 'meta.duration', app: 'duration' },  // Video duration (:15, :30, :60)
  style: { path: 'meta.style', app: 'style' },  // Video style direction
  super: { path: 'meta.super', app: 'super' },  // Video end-frame text
  alternativeSupers: { path: 'meta.alternativeSupers', app: 'alternativeSupers', default: [] },

  // SOCIAL format fields
  hook: { path: 'meta.hook', app: 'hook' },
  sequence: { path: 'meta.sequence', app: 'sequence' },
  caption: { path: 'meta.caption', app: 'caption' },
  alternativeHooks: { path: 'meta.alternativeHooks', app: 'alternativeHooks', default: [] },

  // DIGITAL format fields
  frames: { path: 'meta.frames', app: 'frames' },
  interaction: { path: 'meta.interaction', app: 'interaction' },
  cta: { path: 'meta.cta', app: 'cta' },
  alternativeCTAs: { path: 'meta.alternativeCTAs', app: 'alternativeCTAs', default: [] },

  // EXPERIENTIAL format fields
  setup: { path: 'meta.setup', app: 'setup' },
  participation: { path: 'meta.participation', app: 'participation' },
  reveal: { path: 'meta.reveal', app: 'reveal' },
  alternativeReveals: { path: 'meta.alternativeReveals', app: 'alternativeReveals', default: [] },

  // CAMPAIGN format fields
  campaignLine: { path: 'meta.campaignLine', app: 'campaignLine' },
  manifesto: { path: 'meta.manifesto', app: 'manifesto' },
  extensions: { path: 'meta.extensions', app: 'extensions' },
  alternativeLines: { path: 'meta.alternativeLines', app: 'alternativeLines', default: [] }
};

// ============================================================================
// DB → APP TRANSFORMATIONS
// PHASE 1: Delegating to new models with backward compatibility
// ============================================================================

/**
 * Convert database idea to app format
 * PHASE 1: Now delegates to models/idea.js
 *
 * @param {Object} dbIdea - Raw database record
 * @returns {Object} - Normalized app format idea
 */
export function dbToApp(dbIdea) {
  // Delegate to new model function
  return modelNormalizeIdea(dbIdea);
}

/**
 * Convert app idea to database format for saving
 * PHASE 1: Now delegates to models/idea.js
 *
 * @param {Object} appIdea - App format idea
 * @returns {Object} - Database format with columns + meta object
 */
export function appToDb(appIdea) {
  // Delegate to new model function
  return modelIdeaToDb(appIdea);
}

// ============================================================================
// BATCH TRANSFORMATIONS
// PHASE 1: Delegating to new models
// ============================================================================

/**
 * Convert array of database ideas to app format
 * PHASE 1: Now delegates to models/idea.js
 *
 * @param {Array} dbIdeas - Array of raw database records
 * @returns {Array} - Array of normalized app format ideas
 */
export function dbToAppBatch(dbIdeas) {
  return modelNormalizeIdeas(dbIdeas);
}

/**
 * Convert array of app ideas to database format
 * @param {Array} appIdeas - Array of app format ideas
 * @returns {Array} - Array of database format ideas
 */
export function appToDbBatch(appIdeas) {
  return (appIdeas || []).map(modelIdeaToDb);
}

// ============================================================================
// FIELD ACCESSORS (for when you need either format)
// ============================================================================

/**
 * Get image URL from idea (checks both formats)
 * @param {Object} idea - Idea object
 * @returns {string|null} - Image URL or null
 */
export function getImageUrl(idea) {
  return idea?.imageUrl || idea?.image_url || null;
}

/**
 * Get video URL from idea (checks both formats)
 * @param {Object} idea - Idea object
 * @returns {string|null} - Video URL or null
 */
export function getVideoUrl(idea) {
  return idea?.videoUrl || idea?.video_url || null;
}

/**
 * Get trigger code from idea (checks all variants)
 * @param {Object} idea - Idea object
 * @returns {string} - Trigger code (default: 'A1')
 */
export function getTriggerCode(idea) {
  return idea?.triggerCode || idea?.trigger_code || idea?.trigger || 'A1';
}

/**
 * Get saved state from idea (checks both formats)
 * @param {Object} idea - Idea object
 * @returns {boolean} - Whether idea is saved
 */
export function isSaved(idea) {
  return idea?.saved || idea?.is_saved || false;
}

/**
 * Get refined state from idea (checks both formats)
 * @param {Object} idea - Idea object
 * @returns {boolean} - Whether idea is refined
 */
export function isRefined(idea) {
  return idea?.isRefined || idea?.is_refined || false;
}

/**
 * Get updated timestamp from idea
 * @param {Object} idea - Idea object
 * @returns {string|null} - ISO timestamp or null
 */
export function getUpdatedAt(idea) {
  return idea?.updatedAt || idea?.updated_at || null;
}

/**
 * Get created timestamp from idea
 * @param {Object} idea - Idea object
 * @returns {string|null} - ISO timestamp or null
 */
export function getCreatedAt(idea) {
  return idea?.createdAt || idea?.created_at || null;
}

/**
 * Get session ID from idea (checks both formats)
 * @param {Object} idea - Idea object
 * @returns {string|null} - Session ID or null
 */
export function getSessionId(idea) {
  return idea?.sessionId || idea?.session_id || null;
}

/**
 * Get brand ID from idea (checks both formats)
 * @param {Object} idea - Idea object
 * @returns {string|null} - Brand ID or null
 */
export function getBrandId(idea) {
  return idea?.brandId || idea?.brand_id || null;
}

/**
 * Get product ID from idea (checks both formats)
 * @param {Object} idea - Idea object
 * @returns {string|null} - Product ID or null
 */
export function getProductId(idea) {
  return idea?.productId || idea?.product_id || null;
}

// ============================================================================
// SESSION NORMALIZATION
// ============================================================================

/**
 * Normalize session brief from legacy string or object format
 * @param {Object} session - Session object
 * @returns {Object} - Normalized brief object
 */
export function normalizeBrief(session) {
  if (typeof session.brief === 'object' && session.brief) {
    return {
      product: session.brief.product || '',
      benefit: session.brief.benefit || '',
      formats: session.brief.formats || session.formats || [],
      // V5: Preserve product context for modal context bar
      productId: session.brief.productId || null,
      productData: session.brief.productData || null
    };
  }

  if (typeof session.brief === 'string') {
    return {
      product: session.brief,
      benefit: '',
      formats: session.formats || [],
      productId: null,
      productData: null
    };
  }

  return { product: '', benefit: '', formats: [], productId: null, productData: null };
}

/**
 * Convert database session to app format
 * PHASE 1: Now delegates to models/session.js
 *
 * @param {Object} dbSession - Raw database session
 * @returns {Object} - Normalized app format session
 */
export function dbSessionToApp(dbSession) {
  // Delegate to new model function
  return modelNormalizeSession(dbSession);
}

/**
 * Convert array of database sessions to app format
 * PHASE 1: Now delegates to models/session.js
 *
 * @param {Array} dbSessions - Array of raw database sessions
 * @returns {Array} - Array of normalized app format sessions
 */
export function dbSessionToAppBatch(dbSessions) {
  return modelNormalizeSessions(dbSessions);
}

// ============================================================================
// IMAGE/VIDEO GALLERY NORMALIZATION
// ============================================================================

/**
 * Convert database image record to app format
 * @param {Object} dbImage - Raw database image record
 * @returns {Object} - Normalized app format image
 */
export function dbImageToApp(dbImage) {
  if (!dbImage) return null;

  return {
    id: dbImage.id,
    url: dbImage.url,
    ideaId: dbImage.idea_id || dbImage.ideaId,
    brandId: dbImage.brand_id || dbImage.brandId,
    thumbnailUrl: dbImage.thumbnail_url || dbImage.thumbnailUrl || dbImage.url,
    aspectRatio: dbImage.aspect_ratio || dbImage.aspectRatio,
    width: dbImage.width,
    height: dbImage.height,
    prompt: dbImage.prompt,
    provider: dbImage.provider,
    style: dbImage.style,
    alt: dbImage.alt || '',
    createdAt: dbImage.created_at || dbImage.createdAt
  };
}

/**
 * Convert array of database images to app format
 * @param {Array} dbImages - Array of raw database images
 * @returns {Array} - Array of normalized app format images
 */
export function dbImageToAppBatch(dbImages) {
  return (dbImages || []).map(dbImageToApp);
}

/**
 * Convert database video record to app format
 * @param {Object} dbVideo - Raw database video record
 * @returns {Object} - Normalized app format video
 */
export function dbVideoToApp(dbVideo) {
  if (!dbVideo) return null;

  return {
    id: dbVideo.id,
    url: dbVideo.url,
    ideaId: dbVideo.idea_id || dbVideo.ideaId,
    brandId: dbVideo.brand_id || dbVideo.brandId,
    thumbnailUrl: dbVideo.thumbnail_url || dbVideo.thumbnailUrl,
    duration: dbVideo.duration,
    resolution: dbVideo.resolution,
    prompt: dbVideo.prompt,
    provider: dbVideo.provider,
    isTransferred: dbVideo.is_transferred || dbVideo.isTransferred,
    type: 'video',
    createdAt: dbVideo.created_at || dbVideo.createdAt
  };
}

/**
 * Convert array of database videos to app format
 * @param {Array} dbVideos - Array of raw database videos
 * @returns {Array} - Array of normalized app format videos
 */
export function dbVideoToAppBatch(dbVideos) {
  return (dbVideos || []).map(dbVideoToApp);
}

// ============================================================================
// GENERATOR STATE BRIDGE HELPERS
// ============================================================================

/**
 * Normalize idea for Generator state (includes all Generator-specific fields)
 * This is a superset of dbToApp() with additional computed fields
 *
 * @param {Object} dbIdea - Raw database idea
 * @returns {Object} - Fully normalized idea for GeneratorState
 */
export function normalizeIdeaForGenerator(dbIdea) {
  if (!dbIdea) return null;

  // Start with base conversion
  const base = dbToApp(dbIdea);
  const meta = dbIdea.meta || {};

  // Add Generator-specific fields
  return {
    ...base,
    // Ensure string ID for consistent comparison
    id: String(base.id || dbIdea.id),

    // Alias trigger for backwards compat
    trigger: base.triggerCode,

    // Title fallback chain
    title: base.title || base.headline || 'Untitled',

    // Content aliases
    text: base.visual,

    // Current headline index for navigation
    currentHeadlineIndex: 0,

    // Video-specific convenience fields
    structure: meta.structure || null,
    sceneCount: meta.sceneCount || (meta.scenes?.length) || 0
  };
}

/**
 * Normalize array of ideas for Generator state
 * @param {Array} dbIdeas - Array of raw database ideas
 * @returns {Array} - Array of Generator-ready ideas
 */
export function normalizeIdeasForGenerator(dbIdeas) {
  return (dbIdeas || []).map(normalizeIdeaForGenerator);
}

// ============================================================================
// NORMALIZED IDEA EXPORT (Alias for Q3 implementation)
// ============================================================================

/**
 * Normalize raw idea data from any source (DB, API, legacy)
 * Converts to consistent camelCase format with all field fallbacks resolved
 * This is the main entry point for normalizing ideas from API responses
 *
 * @param {Object} rawIdea - Raw idea from database or API
 * @returns {Object} - Normalized idea with consistent field names
 */
export const normalizeIdea = dbToApp;

/**
 * Normalize an array of ideas
 * PHASE 1: Delegates to models/idea.js
 *
 * @param {Array} rawIdeas - Array of raw ideas from database or API
 * @returns {Array} - Array of normalized ideas
 */
export const normalizeIdeas = dbToAppBatch;

// ============================================================================
// PHASE 1: Re-export brand/product normalization from new models
// ============================================================================

export {
  modelNormalizeBrand as normalizeBrand,
  modelNormalizeBrands as normalizeBrands,
  modelBrandToDb as brandToDb,
  modelNormalizeProduct as normalizeProduct,
  modelNormalizeProducts as normalizeProducts,
  modelProductToDb as productToDb,
  modelNormalizeSession as normalizeSession,
  modelNormalizeSessions as normalizeSessions
};

// ============================================================================
// WINDOW EXPORT (for non-module scripts)
// ============================================================================

if (typeof window !== 'undefined') {
  window.DataMapper = {
    // Idea transformations
    dbToApp,
    appToDb,
    dbToAppBatch,
    appToDbBatch,

    // Field accessors
    getImageUrl,
    getVideoUrl,
    getTriggerCode,
    isSaved,
    isRefined,
    getUpdatedAt,
    getCreatedAt,
    getSessionId,
    getBrandId,
    getProductId,

    // Session normalization
    normalizeBrief,
    dbSessionToApp,
    dbSessionToAppBatch,

    // Gallery normalization
    dbImageToApp,
    dbImageToAppBatch,
    dbVideoToApp,
    dbVideoToAppBatch,

    // Generator helpers
    normalizeIdeaForGenerator,
    normalizeIdeasForGenerator,

    // Primary normalization API (use these for API responses)
    normalizeIdea,
    normalizeIdeas,

    // PHASE 1: Brand and Product normalization
    normalizeBrand: modelNormalizeBrand,
    normalizeBrands: modelNormalizeBrands,
    brandToDb: modelBrandToDb,
    normalizeProduct: modelNormalizeProduct,
    normalizeProducts: modelNormalizeProducts,
    productToDb: modelProductToDb,
    normalizeSession: modelNormalizeSession,
    normalizeSessions: modelNormalizeSessions
  };
}
